/**
 * Base class to be used for popup windows in the main app.  Includes the OperationViewModelMixin so you can do things
 * like ifPanel (if you imagine the popup as a panel).
 */
import OperationViewModelMixin from "common/framework/OperationMixin";

export default kendo.data.ObservableObject.extend(OperationViewModelMixin).extend({

    // flag that controls whether the popup is open.  Use with open binding to open/close popup without
    // needing to interact with the PopUpWindow widget.
    isOpen: false,

    // Optional Deferred object used to indicate that the popup is open.  Useful when working with popups
    // triggered from MainPanelViews.  The open() method in the glue class can return this deferred.
    openDef: undefined,

    // optional validator reference to bind to, and used in the validate method
    validator: undefined,

    init: function() {
        kendo.data.ObservableObject.fn.init.apply(this, arguments);
        this.initializeFirstOp();
    },

    /**
     * Opens the popup
     * @returns {promise}
     */
    open: function() {
        if (this.get("isOpen")) {
            return this.openDef.promise();
        }
        this.startNewOp();
        this.openDef = $.Deferred();
        return $.when(this.onOpen.apply(this, arguments))
            .then(() => {
                // set isOpen after calling onOpen - this allows view models to call things like win().stallOpen().
                this.set("isOpen", this.openDef.state() === "pending");
                return this.openDef.promise();
            });
    },

    /**
     * Passes data back through the deferred without closing the popup
     */
    update: function() {
        this.openDef.resolve.apply(this, arguments);
    },

    /**
     * Rejects the deferred and closes the popup
     */
    cancel: function() {
        this.openDef.reject.apply(this, arguments);
        this.onCancel.apply(this, arguments);
        this._close.apply(this, arguments);
    },

    /**
     * Resolves the deferred and closes the popup
     */
    close: function() {
        this.openDef.resolve.apply(this, arguments);
        this._close.apply(this, arguments);
    },

    _close: function() {
        if(this.get("isOpen")) {
            this.onClose.apply(this, arguments);
        }
        this.markThisComponentAsClosed();
        this.set("isOpen", false);
        this.trigger("windowClosed");
    },

    /**
     * Abstract
     * called when the popup is canceled
     */
    onCancel: function() {},

    /**
     * Abstract
     * called when the popup is opened
     */
    onOpen: function() {},

    /**
     * Abstract
     * called when the popup is closed (called in addition to cancel if popup is canceled)
     */
    onClose: function() {},

    /**
     * Runs basic validation on the form referenced by the validator. Override if more advanced
     * validation is necessary. You are responsible for calling validate() when needed.
     */
    validate: function() {
        return this.validator().validate();
    }

});
